/** Member-info/AutoPay data-access layer — billing profile, AutoPay summary, and enrollment. */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  EnableMyAutoPayDocument,
  type EnableMyAutoPayFailureCode,
  MyMemberInfoDocument,
  type MyMemberInfoQuery,
} from '@/graphql/generated/graphql';

import type { BillingAddressValues } from '@/lib/payments/paymentAccount.types';

export interface MemberBillingProfile {
  savedAddress: BillingAddressValues | null;
}

export interface MemberAutoPaySummary {
  autoPayEnrolled: boolean;
  /**
   * Recurring AutoPay amount, as shown in the UI. Sourced from `currentBalanceFormula`
   * (legacy `Current_Balance_Formula__c`) — matches legacy's own AutoPay modal, which displays
   * this same field under the label "Amount".
   */
  autoPayAmount: number | null;
  /** Payment plan / frequency (legacy `Current_Agreement_Payment_Schedule__c`). */
  autoPayPlan: string | null;
}

export type MyMemberInfo = MyMemberInfoQuery['myMemberInfo'];

/**
 * Shared by {@link useMemberBillingProfile} and {@link useMemberAutoPaySummary} (same queryKey,
 * different `select`) so mounting both against one `myMemberInfo` query doesn't fire it twice, and
 * by the add-card/add-bank-account mutations so one invalidate refreshes both views.
 */
export const memberInfoKey = ['payments', 'myMemberInfo'] as const;

export async function requestMyMemberInfo(): Promise<MyMemberInfo> {
  const data = await gqlRequest(MyMemberInfoDocument);
  return data.myMemberInfo;
}

export function mapToBillingProfile(member: MyMemberInfo): MemberBillingProfile {
  const mailingAddress = member?.mailingAddress;
  if (!mailingAddress) {
    return { savedAddress: null };
  }
  return {
    savedAddress: {
      name: `${member?.firstName ?? ''} ${member?.lastName ?? ''}`.trim(),
      line1: mailingAddress.line1 ?? '',
      line2: mailingAddress.line2 ?? '',
      city: mailingAddress.city ?? '',
      state: mailingAddress.state ?? '',
      zip: mailingAddress.zip ?? '',
    },
  };
}

export function mapToAutoPaySummary(member: MyMemberInfo): MemberAutoPaySummary {
  return {
    autoPayEnrolled: member?.autoPay ?? false,
    autoPayAmount: member?.currentBalanceFormula ?? null,
    autoPayPlan: member?.currentAgreementPaymentSchedule ?? null,
  };
}

export interface CurrentBalance {
  /**
   * Outstanding (positive), credit (negative), or paid-up (zero) balance. `null` means the value
   * is genuinely unknown — kept distinct from `0` so a data gap can't render as "you're all paid
   * up".
   */
  amount: number | null;
  /** Due date of the oldest unpaid invoice, or `null` if there isn't one on file. */
  dueDate: string | null;
  /** Backend-computed days past due, passed through as-is — never re-derived client-side. */
  daysPastDue: number | null;
}

export function mapToCurrentBalance(member: MyMemberInfo): CurrentBalance {
  return {
    amount: member?.currentBalanceFormula ?? null,
    dueDate: member?.dateOfOldestUnpaidInvoice ?? null,
    daysPastDue: member?.billingDaysPastDue ?? null,
  };
}

/**
 * Thrown when enableMyAutoPay returns its Failure variant. `ALREADY_ENABLED` is the only code —
 * callers treat it as a no-op, not a user-facing error (see useAddCreditCard/useAddBankAccount).
 */
export class EnableMyAutoPayError extends Error {
  constructor(public readonly code: EnableMyAutoPayFailureCode) {
    super(code);
    this.name = 'EnableMyAutoPayError';
  }
}

/**
 * Enrolls the caller in AutoPay. No-arg and member-scoped — not tied to any card/bank profile.
 * Idempotent: enableMyAutoPay treats an already-enrolled member as a no-op Failure, not an error.
 */
export async function requestEnableMyAutoPay(): Promise<void> {
  const data = await gqlRequest(EnableMyAutoPayDocument);
  const result = data.enableMyAutoPay;
  if (result.__typename === 'EnableMyAutoPayFailure') {
    throw new EnableMyAutoPayError(result.code);
  }
}
